import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ChevronDown, Mountain, Waves, Compass, Sparkles, ArrowRight, Play, Youtube, ExternalLink } from "lucide-react";
import { EPISODES as FALLBACK_EPISODES, SEASON_2_PINS as FALLBACK_PINS, VIJAY_DATLA_PORTRAIT, YOUTUBE_CHANNEL } from "../lib/episodes";
import { LOGOS, ETV_WIN_SERIES_URL } from "../lib/logos";
import { Globe3D } from "../components/Globe3D";
import { CinematicEarth } from "../components/CinematicEarth";
import { EpisodeReel } from "../components/EpisodeReel";
import { ReelModal } from "../components/ReelModal";
import { Reveal } from "../components/Reveal";
import { GandabherundaMark } from "../components/GandabherundaMark";
import { ImpactDashboard } from "../components/ImpactDashboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_POSTER =
  "https://customer-assets.emergentagent.com/job_virtual-journey-hub-1/artifacts/6wzlmpzr_Screenshot%202026-05-20%20172129.png";

const PILLARS = [
  { icon: Compass,  label: "Spiritual Journeys", desc: "Sacred places, ancient traditions, divine stories." },
  { icon: Mountain, label: "Historical Trails",  desc: "Forgotten kingdoms, timeless legacies, rich heritage." },
  { icon: Waves,    label: "Adventure Expeditions", desc: "Thrills that challenge the body, awaken the soul." },
  { icon: Sparkles, label: "Heartwarming Conversations", desc: "Strip away distractions. Meet the real person." },
];

export default function Home() {
  const [episodes, setEpisodes] = useState(FALLBACK_EPISODES);
  const [futurePins, setFuturePins] = useState(FALLBACK_PINS);
  const [openEp, setOpenEp] = useState(null);

  useEffect(() => {
    axios.get(`${API}/episodes`).then((r) => setEpisodes(r.data)).catch(() => {});
    axios.get(`${API}/season2-pins`).then((r) => setFuturePins(r.data)).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page" className="relative">
      {/* ═══════════════════════════════════════════════════════════════
         §1 HERO — CINEMATIC EARTH (deep space → earth-rise → India)
      ═══════════════════════════════════════════════════════════════ */}
      <section
        data-testid="hero-section"
        className="relative w-screen overflow-hidden"
        style={{ height: "100vh", background: "#02030a" }}
      >
        <CinematicEarth
          episodes={episodes}
          futurePins={futurePins}
          onSelect={(ep) => setOpenEp(ep)}
        />

        {/* Top-left mission badge */}
        <div className="absolute top-24 left-6 md:left-12 z-[10] hidden md:block px-3 py-2"
             style={{ background: "rgba(5,4,3,.55)", backdropFilter: "blur(10px)", border: "1px solid rgba(201,160,74,.3)" }}>
          <div className="st-cinzel text-[0.5rem] tracking-[0.4em] uppercase text-[var(--gold-lt)]">MISSION · S01 LIVE</div>
          <div className="mt-1 st-infant text-[0.85rem] text-[var(--cream)]">06 destinations · 06 stories</div>
          <div className="mt-2 flex items-center gap-2">
            <span className="block w-1.5 h-1.5 rounded-full bg-[#ff7a7a] animate-pulse"/>
            <span className="st-cinzel text-[0.45rem] tracking-[0.3em] text-[var(--cream)] uppercase">STREAMING ON ETV WIN</span>
          </div>
        </div>

        {/* Top-right coordinates */}
        <div className="absolute top-24 right-6 md:right-12 z-[10] hidden md:block text-right px-3 py-2"
             style={{ background: "rgba(5,4,3,.55)", backdropFilter: "blur(10px)", border: "1px solid rgba(201,160,74,.3)" }}>
          <div className="st-cinzel text-[0.5rem] tracking-[0.4em] uppercase text-[var(--gold-lt)]">TARGET</div>
          <div className="mt-1 st-infant text-[0.85rem] text-[var(--cream)]">20.5937° N · 78.9629° E</div>
          <div className="mt-1 st-cinzel text-[0.45rem] tracking-[0.3em] uppercase text-[var(--cream)]">BHARAT</div>
        </div>

        {/* Bottom CTAs */}
        <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-[10] flex flex-wrap gap-3 justify-center px-5" style={{ animation: "heroFade 1.4s 3.5s both" }}>
          <a
            href={ETV_WIN_SERIES_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="st-btn st-interactive"
            data-testid="hero-cta-etvwin"
            style={{
              background: "#e21e26",
              borderColor: "#e21e26",
              color: "#ffffff",
              boxShadow: "0 8px 28px rgba(226,30,38,.3)",
            }}
          >
            <Play size={11} fill="currentColor"/> Watch Full Episodes on ETV Win
          </a>
          <Link to="/episodes" className="st-btn st-btn-solid st-interactive" data-testid="hero-cta-watch">
            <Play size={11} fill="currentColor"/> Trailers
          </Link>
          <a
            href={YOUTUBE_CHANNEL}
            target="_blank"
            rel="noopener noreferrer"
            className="st-btn st-interactive"
            data-testid="hero-cta-youtube"
            style={{ borderColor: "rgba(255,80,80,.6)", color: "#ff9595", background: "rgba(5,4,3,.5)", backdropFilter: "blur(8px)" }}
          >
            <Youtube size={12}/> YouTube Channel
          </a>
          <Link to="/about" className="st-btn st-interactive" data-testid="hero-cta-about"
                style={{ background: "rgba(5,4,3,.5)", backdropFilter: "blur(8px)" }}>
            Read the Story
          </Link>
        </div>

        {/* Scroll hint */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[10] flex flex-col items-center gap-2" style={{ animation: "heroFade 1.4s 4s both" }}>
          <span className="st-cinzel text-[0.46rem] tracking-[0.5em] uppercase text-[var(--gold-lt)]">Scroll</span>
          <ChevronDown size={13} className="text-[var(--gold)] animate-bounce"/>
        </div>

        <style>{`@keyframes heroFade { from { opacity: 0; transform: translateY(28px); } to { opacity: 1; transform: none; } }`}</style>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
         §1.5 OFFICIAL TITLE CARD — Gandabherunda poster
      ═══════════════════════════════════════════════════════════════ */}
      <section
        data-testid="title-card-section"
        className="relative w-screen overflow-hidden"
        style={{ height: "min(78vh, 880px)", background: "#cfe3ef" }}
      >
        <img
          src={HERO_POSTER}
          alt="Soul Trip — A Travel Based Adventure Talk Show"
          className="absolute inset-0 w-full h-full"
          style={{
            objectFit: "cover",
            objectPosition: "center 35%",
            animation: "titleZoom 28s ease-in-out infinite alternate",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, var(--coal) 0%, transparent 12%, transparent 80%, var(--coal) 100%)",
          }}
        />
        <style>{`@keyframes titleZoom { from { transform: scale(1.0); } to { transform: scale(1.06); } }`}</style>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
         §2 THE 3D GLOBE — mission map
      ═══════════════════════════════════════════════════════════════ */}
      <section
        data-testid="globe-section"
        className="relative w-screen min-h-screen flex flex-col items-center overflow-hidden"
        style={{ background: "var(--dusk)" }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 70% 60% at 50% 50%, rgba(201,160,74,.05) 0%, transparent 65%), repeating-linear-gradient(0deg, transparent, transparent 59px, rgba(201,160,74,.025) 60px), repeating-linear-gradient(90deg, transparent, transparent 59px, rgba(201,160,74,.025) 60px)",
          }}
        />

        <Reveal className="relative z-10 text-center pt-24 px-5">
          <span className="st-eyebrow">Mission Map · Season I & II</span>
          <h2 className="mt-3 st-cormorant text-3xl md:text-5xl italic font-light text-[var(--cream)]">
            Where Soul Trip has been —
            <em className="block not-italic st-cinzel font-medium text-[var(--gold-lt)] mt-2 tracking-tight text-2xl md:text-4xl">
              and where it goes next.
            </em>
          </h2>
          <div className="st-rule mx-auto mt-5"/>
          <p className="mt-5 st-infant text-base md:text-lg text-[var(--fog)] max-w-xl mx-auto">
            Drag the earth. Tap a <span className="text-[var(--gold)]">gold pin</span> for a Season 1 story.
            Watch the <span className="text-[#9ED4F2]">holographic markers</span> bloom — Season 2 is being charted.
          </p>
        </Reveal>

        <div className="relative z-10 w-full flex justify-center py-8 px-5">
          <Globe3D
            episodes={episodes}
            futurePins={futurePins}
            onSelect={(ep) => setOpenEp(ep)}
          />
        </div>

        {/* Legend */}
        <div className="relative z-10 flex flex-wrap gap-6 justify-center pb-12 px-5">
          <div className="flex items-center gap-2 st-cinzel text-[0.52rem] tracking-[0.35em] uppercase text-[var(--fog)]">
            <span className="w-2.5 h-2.5 rounded-full bg-[var(--gold-lt)] shadow-[0_0_10px_rgba(232,197,106,0.7)]"/>
            Season 1 · Live
          </div>
          <div className="flex items-center gap-2 st-cinzel text-[0.52rem] tracking-[0.35em] uppercase text-[var(--fog)]">
            <span className="w-2.5 h-2.5 rounded-full bg-[#9ED4F2] shadow-[0_0_10px_rgba(106,182,232,0.7)] animate-pulse"/>
            Season 2 · Inbound
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
         §3 EPISODE REELS — 9:16 vertical cards
      ═══════════════════════════════════════════════════════════════ */}
      <section
        data-testid="reels-section"
        className="relative w-screen"
        style={{ background: "var(--coal)" }}
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "url('https://images.unsplash.com/photo-1519681393784-d120267933ba?w=2400&q=80&auto=format&fit=crop') center/cover no-repeat",
            filter: "brightness(.12) saturate(.3)",
            opacity: 0.9,
          }}
        />

        <div className="relative z-[2] px-5 md:px-16 py-24 md:py-32">
          <Reveal className="text-center mb-16">
            <span className="st-eyebrow">Season One · 06 Episodes · ETV Win</span>
            <h2 className="mt-3 st-cormorant text-3xl md:text-5xl italic font-light text-[var(--cream)]">
              Official trailers. <em className="not-italic text-[var(--gold-lt)] st-cinzel font-medium tracking-tight">All six.</em>
            </h2>
            <div className="st-rule mx-auto mt-5"/>
            <p className="mt-5 st-infant text-base md:text-lg text-[var(--fog)] max-w-lg mx-auto">
              "Once a year go someplace you've never been before." — Dalai Lama
            </p>
          </Reveal>

          <div
            data-testid="reels-grid"
            className="grid gap-6 md:gap-8"
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))" }}
          >
            {episodes.map((ep, i) => (
              <EpisodeReel key={ep.id} episode={ep} index={i} onOpen={(e) => setOpenEp(e)} />
            ))}
          </div>

          <Reveal className="mt-16 text-center" delay={150}>
            <Link to="/episodes" className="st-btn st-interactive" data-testid="home-view-all-episodes">
              View all episodes <ArrowRight size={12}/>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
         §4 PILLARS — what we do
      ═══════════════════════════════════════════════════════════════ */}
      <section
        data-testid="pillars-section"
        className="relative w-screen py-24 md:py-32 px-5 md:px-16"
        style={{ background: "var(--dusk)" }}
      >
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <span className="st-eyebrow">The Four Pillars</span>
            <h2 className="mt-3 st-cormorant text-3xl md:text-5xl italic font-light text-[var(--cream)] max-w-3xl">
              Soul Trip is what happens when you <em className="not-italic text-[var(--gold-lt)]">strip away distractions</em>.
            </h2>
            <div className="st-rule mt-5"/>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px" style={{ background: "rgba(201,160,74,.1)" }}>
            {PILLARS.map((p, i) => (
              <Reveal
                key={p.label}
                delay={i * 80}
                className="p-8 md:p-12 group transition-colors"
                style={{ background: "rgba(8,6,4,.85)" }}
              >
                <div className="flex items-start gap-5">
                  <div
                    className="w-12 h-12 flex items-center justify-center shrink-0"
                    style={{ border: "1px solid rgba(201,160,74,.4)", background: "rgba(201,160,74,.06)" }}
                  >
                    <p.icon size={18} className="text-[var(--gold)]"/>
                  </div>
                  <div>
                    <div className="st-cinzel text-[0.62rem] tracking-[0.32em] text-[var(--gold)] uppercase font-semibold">
                      0{i+1} · {p.label}
                    </div>
                    <p className="mt-3 st-infant text-[1.05rem] text-[var(--fog)] leading-relaxed">
                      {p.desc}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
         §5 HOST — Vijay Datla
      ═══════════════════════════════════════════════════════════════ */}
      <section
        data-testid="host-section"
        className="relative w-screen overflow-hidden py-24 md:py-32 px-5 md:px-16"
        style={{ background: "var(--coal)" }}
      >
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-12">
            <span className="st-eyebrow">The Man Behind The Mic</span>
            <h2 className="mt-3 st-cormorant text-3xl md:text-5xl italic font-light text-[var(--cream)]">
              Vijay Datla — <em className="not-italic text-[var(--gold-lt)]">an actor. a director. a traveller who finally followed the road.</em>
            </h2>
            <div className="st-rule mx-auto mt-5"/>
          </Reveal>

          <Reveal>
            <div
              className="relative w-full mx-auto"
              style={{
                background: "var(--night)",
                border: "1px solid rgba(201,160,74,.22)",
                overflow: "hidden",
                maxWidth: 1100,
              }}
            >
              <img
                src={VIJAY_DATLA_PORTRAIT}
                alt="Vijay Datla — host, concept and direction of Soul Trip"
                className="block w-full h-auto"
                style={{ display: "block" }}
              />
            </div>
          </Reveal>

          <Reveal delay={120} className="mt-10 grid md:grid-cols-2 gap-8 md:gap-14 max-w-5xl mx-auto">
            <div>
              <div className="st-cinzel text-[0.5rem] tracking-[0.4em] uppercase text-[var(--gold)]">Host · Concept · Direction</div>
              <div className="mt-2 st-cormorant italic text-2xl md:text-3xl text-[var(--cream)]">Vijay Datla</div>
              <p className="mt-5 st-infant text-[1.05rem] text-[var(--fog)] leading-relaxed">
                Vijay Datla cut his teeth in theatre, then walked onto Telugu film sets as an assistant
                director and later as a lead. Between takes he kept disappearing into forests, monasteries
                and coastlines — collecting the kind of stories that don't make it into scripts.
              </p>
            </div>
            <div>
              <p className="st-infant text-[1.05rem] text-[var(--fog)] leading-relaxed">
                <em className="text-[var(--gold-lt)] not-italic">Soul Trip</em> is what came back with him.
                Six destinations. Six guests. One question: when everything else is taken away — what's left of us?
              </p>
              <Link to="/about" className="mt-6 inline-flex items-center gap-2 st-btn st-interactive" data-testid="host-read-more">
                The full story <ArrowRight size={12}/>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
         §6.5 OFFICIAL CREDITS — Streaming · Powered · Presented · Produced · Music
      ═══════════════════════════════════════════════════════════════ */}
      <section
        data-testid="credits-section"
        className="relative w-screen py-24 md:py-32 px-5 md:px-16 overflow-hidden"
        style={{ background: "var(--dusk)" }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 60% 60% at 50% 50%, rgba(201,160,74,.06) 0%, transparent 70%)",
          }}
        />
        <div className="relative z-[2] max-w-6xl mx-auto">
          <Reveal className="text-center">
            <span className="st-eyebrow">First Time in India · One of its Kind Show</span>
            <h2 className="mt-3 st-cormorant text-3xl md:text-5xl italic font-light text-[var(--cream)]">
              The voices behind
              <em className="block not-italic st-cinzel font-black tracking-tight text-[var(--gold-lt)] mt-2 text-4xl md:text-6xl">
                SOUL TRIP
              </em>
            </h2>
            <div className="st-rule mx-auto mt-5"/>
            <p className="mt-6 st-cormorant italic text-lg md:text-xl text-[var(--fog)] max-w-2xl mx-auto leading-relaxed">
              "Once a year go someplace you've never been before."
              <span className="block st-cinzel text-[0.5rem] tracking-[0.4em] uppercase text-[var(--gold-dk)] mt-3">— Dalai Lama</span>
            </p>
          </Reveal>

          {/* Credits grid */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-px" style={{ background: "rgba(201,160,74,.14)" }}>
            {/* Streaming On — col-span-3 */}
            <Reveal delay={0} className="md:col-span-3 p-7 md:p-8 flex flex-col" style={{ background: "var(--coal)" }}>
              <div className="st-cinzel text-xs md:text-sm font-bold tracking-[0.32em] uppercase text-[var(--gold)]">Streaming On</div>
              <div className="mt-5 flex items-center justify-center" style={{ minHeight: 84 }}>
                <img
                  src={LOGOS.etvWin}
                  alt="ETV Win — streaming platform"
                  className="block"
                  style={{ height: 84, width: "auto", objectFit: "contain" }}
                />
              </div>
              <div className="mt-5 pt-4" style={{ borderTop: "1px solid rgba(201,160,74,.12)" }}>
                <div className="flex items-center gap-3">
                  <img
                    src={LOGOS.tv9}
                    alt="TV9 Telugu"
                    className="block shrink-0"
                    style={{ height: 52, width: "auto", objectFit: "contain" }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="st-cormorant text-xl md:text-2xl font-bold text-[var(--cream)] leading-tight">TV9 Telugu</div>
                    <div className="st-cinzel text-xs md:text-sm font-bold tracking-[0.28em] uppercase text-[var(--fog)] mt-1">Broadcasting Partner</div>
                  </div>
                </div>
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5" style={{ background: "rgba(106,182,232,.1)", border: "1px solid rgba(106,182,232,.35)" }}>
                  <span className="block w-2 h-2 rounded-full bg-[#9ED4F2] animate-pulse"/>
                  <span className="st-cinzel text-xs md:text-sm font-bold tracking-[0.24em] text-[#9ED4F2] uppercase">Streaming Soon</span>
                </div>
              </div>
            </Reveal>

            {/* Season 1 Partners — combined Rizwan + Tamada logo (col-span-4) */}
            <Reveal delay={60} className="md:col-span-4 p-7 md:p-8 flex flex-col" style={{ background: "var(--coal)" }}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="st-cinzel text-xs md:text-sm font-bold tracking-[0.32em] uppercase text-[var(--gold)]">Presented · Powered By</div>
                <div className="st-cinzel text-[0.65rem] md:text-xs font-semibold tracking-[0.28em] uppercase text-[var(--gold-dk)]">Season 1 Only</div>
              </div>
              <div className="flex-1 mt-4 flex items-center justify-center" style={{ minHeight: 120 }}>
                <img
                  src={LOGOS.rizwanTamada}
                  alt="Rizwan Entertainment presents · Powered by Tamada Media"
                  className="block w-full h-auto"
                  style={{ maxHeight: 130, objectFit: "contain" }}
                />
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3 text-center">
                <div>
                  <div className="st-cormorant text-xl md:text-2xl font-bold text-[var(--gold-lt)] leading-tight">Rizwan Entertainments</div>
                  <div className="st-cinzel text-xs md:text-sm font-bold tracking-[0.28em] uppercase text-[var(--fog)] mt-1.5">Presents</div>
                </div>
                <div>
                  <div className="st-cormorant text-xl md:text-2xl font-bold text-[var(--gold-lt)] leading-tight">Tamada Media</div>
                  <div className="st-cinzel text-xs md:text-sm font-bold tracking-[0.28em] uppercase text-[var(--fog)] mt-1.5">Powered By</div>
                </div>
              </div>
            </Reveal>

            {/* Produced By — Gandabherunda Arts (hero credit, col-span-3) */}
            <Reveal delay={120} className="md:col-span-3 p-7 md:p-8 flex flex-col" style={{ background: "rgba(201,160,74,.05)" }}>
              <div className="st-cinzel text-xs md:text-sm font-bold tracking-[0.32em] uppercase text-[var(--gold-lt)]">Produced By</div>
              <div className="flex-1 mt-4 flex items-center justify-center" style={{ minHeight: 120 }}>
                <img
                  src={LOGOS.gandabherunda}
                  alt="Gandabherunda Arts"
                  className="block"
                  style={{ maxHeight: 140, width: "auto", objectFit: "contain" }}
                />
              </div>
              <div className="mt-3 st-cormorant text-2xl md:text-3xl font-bold text-[var(--cream)] text-center leading-tight">
                Gandabherunda Arts
              </div>
              <div className="mt-2 st-cinzel text-xs md:text-sm font-semibold tracking-[0.26em] uppercase text-[var(--gold)] text-center">
                Season 1 · Season 2 · Future Seasons
              </div>
            </Reveal>

            {/* Music — K-Music (col-span-2) */}
            <Reveal delay={180} className="md:col-span-2 p-7 md:p-8 flex flex-col items-center justify-center text-center" style={{ background: "var(--coal)" }}>
              <div className="st-cinzel text-xs md:text-sm font-bold tracking-[0.32em] uppercase text-[var(--gold)]">Music</div>
              <div className="flex-1 mt-4 flex items-center justify-center" style={{ minHeight: 120 }}>
                <img
                  src={LOGOS.kMusic}
                  alt="K-Music"
                  className="block"
                  style={{ maxHeight: 130, width: "auto", objectFit: "contain" }}
                />
              </div>
              <div className="mt-3 st-cormorant text-2xl md:text-3xl font-bold text-[var(--cream)] leading-tight">
                K-Music
              </div>
              <div className="mt-1 st-cormorant italic text-base md:text-lg font-medium text-[var(--fog)]">
                by Sahithya Sagar
              </div>
            </Reveal>
          </div>

          {/* Concept / Host */}
          <Reveal delay={280} className="mt-14 grid md:grid-cols-3 gap-8 md:gap-10 text-left">
            <div>
              <div className="st-cinzel text-xs md:text-sm font-bold tracking-[0.32em] uppercase text-[var(--gold)]">Concept · Host · Direction</div>
              <div className="mt-2 st-cormorant text-2xl md:text-3xl font-bold text-[var(--cream)] leading-tight">Vijay Datla</div>
            </div>
            <div>
              <div className="st-cinzel text-xs md:text-sm font-bold tracking-[0.32em] uppercase text-[var(--gold)]">Co-Director</div>
              <div className="mt-2 st-cormorant text-2xl md:text-3xl font-bold text-[var(--cream)] leading-tight">Kanna Sri Krishna · Rajendra D</div>
            </div>
            <div>
              <div className="st-cinzel text-xs md:text-sm font-bold tracking-[0.32em] uppercase text-[var(--gold)]">DOP · Edit · Music</div>
              <div className="mt-2 st-cormorant text-2xl md:text-3xl font-bold text-[var(--cream)] leading-tight">Atchut Varma · Anil Alluri · Sahithya Sagar</div>
            </div>
          </Reveal>

          {/* Crew / Cast roll — readable bold treatment */}
          <Reveal delay={340} className="mt-12 max-w-4xl mx-auto text-center">
            <div className="st-cinzel text-xs md:text-sm font-bold tracking-[0.32em] uppercase text-[var(--gold)]">
              Crew · Production · Sound
            </div>
            <p className="mt-4 st-cormorant text-lg md:text-2xl font-semibold text-[var(--cream)] leading-relaxed">
              P.U.N Varma <span className="text-[var(--gold-dk)]">·</span> Kushi <span className="text-[var(--gold-dk)]">·</span> Chaitanya Kalidindi <span className="text-[var(--gold-dk)]">·</span> Kolli Gopala Krishna <span className="text-[var(--gold-dk)]">·</span> Raveen Ramaraju <span className="text-[var(--gold-dk)]">·</span> Chella Sambaiah <span className="text-[var(--gold-dk)]">·</span> Gunda Prabhakar <span className="text-[var(--gold-dk)]">·</span> Uday Sandeep <span className="text-[var(--gold-dk)]">·</span> Naga <span className="text-[var(--gold-dk)]">·</span> Haridasu Shruthi Naidu <span className="text-[var(--gold-dk)]">·</span> V Ravi Chandra Karnati <span className="text-[var(--gold-dk)]">·</span> Sai Mudunuri <span className="st-cinzel text-xs md:text-sm font-bold tracking-[0.2em] uppercase text-[var(--gold-lt)] ml-1">(Sound)</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
         §6.75 SEASON 1 END CARD — cinematic wrap poster
      ═══════════════════════════════════════════════════════════════ */}
      <ImpactDashboard />

      <section
        data-testid="s1-end-card-section"
        className="relative w-screen py-20 md:py-28 px-5 md:px-16 overflow-hidden"
        style={{ background: "var(--coal)" }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 50% 60% at 50% 50%, rgba(201,160,74,.08) 0%, transparent 70%)",
          }}
        />
        <div className="relative z-[2] max-w-6xl mx-auto">
          <Reveal className="text-center">
            <span className="st-eyebrow">Season 1 · Wrap</span>
            <h2 className="mt-3 st-cormorant text-3xl md:text-5xl italic font-light text-[var(--cream)]">
              The Journey That Was
            </h2>
            <div className="st-rule mx-auto mt-5"/>
            <p className="mt-5 st-cormorant italic text-base md:text-lg text-[var(--fog)] max-w-2xl mx-auto leading-relaxed">
              Six destinations · Six stories · One unforgettable season. A salute to every soul who built Soul Trip.
            </p>
          </Reveal>

          <Reveal delay={120} className="mt-10 md:mt-14">
            <figure
              className="relative mx-auto overflow-hidden"
              style={{
                maxWidth: "1100px",
                border: "1px solid rgba(201,160,74,.28)",
                boxShadow:
                  "0 30px 80px rgba(0,0,0,.55), 0 0 0 1px rgba(201,160,74,.08) inset",
                background: "var(--ink)",
              }}
            >
              <img
                src={LOGOS.s1EndCard}
                alt="Soul Trip — Season 1 official end card poster with full crew credits"
                data-testid="s1-end-card-img"
                className="block w-full h-auto"
                style={{ objectFit: "contain" }}
                loading="lazy"
              />
              {/* Gold corner accents */}
              <span className="absolute top-0 left-0 w-6 h-6 border-t border-l border-[var(--gold)] opacity-60" />
              <span className="absolute top-0 right-0 w-6 h-6 border-t border-r border-[var(--gold)] opacity-60" />
              <span className="absolute bottom-0 left-0 w-6 h-6 border-b border-l border-[var(--gold)] opacity-60" />
              <span className="absolute bottom-0 right-0 w-6 h-6 border-b border-r border-[var(--gold)] opacity-60" />
            </figure>
          </Reveal>

          <Reveal delay={220} className="mt-8 text-center st-cinzel text-xs md:text-sm font-bold tracking-[0.32em] uppercase text-[var(--gold-dk)]">
            A Gandabherunda Arts Production · Season 1 Closed
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
         §7 SEASON 2 — coming soon
      ═══════════════════════════════════════════════════════════════ */}
      <section
        data-testid="season2-section"
        className="relative w-screen py-24 md:py-32 px-5 md:px-16 overflow-hidden"
        style={{
          background:
            "linear-gradient(180deg, var(--coal) 0%, #050708 70%, var(--coal) 100%)",
        }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at 30% 50%, rgba(106,182,232,.07) 0%, transparent 50%), radial-gradient(circle at 80% 30%, rgba(201,160,74,.05) 0%, transparent 45%)",
          }}
        />

        <Reveal className="relative z-[2] max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-5">
            <span className="block w-2.5 h-2.5 rounded-full bg-[#9ED4F2] animate-pulse shadow-[0_0_15px_rgba(158,212,242,0.8)]"/>
            <span className="st-cinzel text-[0.5rem] tracking-[0.45em] uppercase text-[#9ED4F2]">
              S/02 · INBOUND
            </span>
          </div>
          <h2 className="st-cormorant text-3xl md:text-5xl italic font-light text-[var(--cream)] leading-tight">
            More destinations. More stories.
            <em className="not-italic text-[var(--gold-lt)] block st-cinzel font-medium tracking-tight text-2xl md:text-4xl mt-3">
              More soul.
            </em>
          </h2>
          <div className="st-rule mx-auto mt-5"/>
          <p className="mt-5 st-infant text-base md:text-lg text-[var(--fog)]">
            Season 2 is being charted across the Trans-Himalaya, Brahmaputra and beyond.
            Drop your email — be the first to know when the next signal goes live.
          </p>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            {futurePins.map((p) => (
              <div
                key={p.id}
                className="p-4 text-left"
                style={{
                  background: "rgba(106,182,232,.06)",
                  border: "1px solid rgba(106,182,232,.22)",
                  backdropFilter: "blur(6px)",
                }}
              >
                <div className="st-cinzel text-[0.5rem] tracking-[0.3em] uppercase text-[#9ED4F2]">
                  {p.id.toUpperCase()}
                </div>
                <div className="st-cormorant text-[var(--cream)] text-base font-medium mt-1">{p.label}</div>
                <div className="st-infant text-[0.85rem] text-[var(--fog)] mt-1">{p.region}</div>
              </div>
            ))}
          </div>

          <div className="mt-10">
            <Link to="/contact" className="st-btn st-interactive" data-testid="s2-cta">
              Notify me when S2 drops <ArrowRight size={12}/>
            </Link>
          </div>
        </Reveal>
      </section>

      {openEp && <ReelModal episode={openEp} onClose={() => setOpenEp(null)} />}
    </main>
  );
}
